import { motion } from 'framer-motion';
import { MapPin, Mail, Phone, Clock, ExternalLink, Send, Building, Users } from 'lucide-react';
import { useState } from 'react';
import { useLanguage } from '../../../context/LanguageContext';

const DSB_LOGO = '/images/edited-dsb-logo.png';

const T = {
  heroLabel: { de: 'Kontakt', en: 'Contact' },
  heroTitle1: { de: 'Beratungsgespräch', en: 'Consultation' },
  heroTitle2: { de: 'vereinbaren', en: 'Schedule' },
  heroSub: { de: 'Vereinbaren Sie ein unverbindliches Erstgespräch mit unseren Experten.', en: 'Schedule a non-binding initial meeting with our experts.' },
  address: { de: 'Adresse', en: 'Address' },
  email: { de: 'E-Mail', en: 'Email' },
  phone: { de: 'Telefon', en: 'Phone' },
  website: { de: 'Website', en: 'Website' },
  availability: { de: 'Erreichbarkeit', en: 'Availability' },
  availVal: { de: 'Mo–Fr: 09:00 – 17:00 Uhr', en: 'Mon–Fri: 09:00 – 17:00' },
  mgmt: { de: 'Geschäftsführung', en: 'Management' },
  mgmtSub: { de: 'Gesellschafter der Basic United Holding GmbH', en: 'Partners of Basic United Holding GmbH' },
  formTitle: { de: 'Beratungsanfrage', en: 'Consulting Request' },
  formSub: { de: 'Füllen Sie das Formular aus und wir melden uns bei Ihnen.', en: 'Fill out the form and we will get back to you.' },
  name: { de: 'Ihr Name *', en: 'Your Name *' },
  company: { de: 'Unternehmen', en: 'Company' },
  emailLabel: { de: 'E-Mail *', en: 'Email *' },
  phonePh: { de: 'Telefon', en: 'Phone' },
  topic: { de: 'Beratungsthema', en: 'Consulting Topic' },
  topicPh: { de: 'Bitte wählen...', en: 'Please select...' },
  topicOther: { de: 'Sonstiges', en: 'Other' },
  message: { de: 'Ihre Nachricht *', en: 'Your Message *' },
  msgPh: { de: 'Beschreiben Sie kurz Ihr Anliegen...', en: 'Briefly describe your concern...' },
  submit: { de: 'Anfrage senden', en: 'Send Request' },
  privacyNote: { de: 'Mit dem Absenden erklären Sie sich mit unserer Datenschutzerklärung einverstanden.', en: 'By submitting, you agree to our privacy policy.' },
  thankYou: { de: 'Vielen Dank für Ihre Anfrage! Wir melden uns innerhalb von 24 Stunden bei Ihnen.', en: 'Thank you for your enquiry! We will get back to you within 24 hours.' },
};

export default function DSBBusinessKontakt() {
  const [formData, setFormData] = useState({ name: '', company: '', email: '', phone: '', topic: '', message: '' });
  const { language } = useLanguage();

  const handleSubmit = (e) => {
    e.preventDefault();
    alert(T.thankYou[language]);
  };

  return (
    <main data-testid="sub-dsb-business-kontakt" className="bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-[#f8fafc] to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <span className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4 block">{T.heroLabel[language]}</span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0F1A2E] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {T.heroTitle1[language]}<br /><span className="text-[#F5A623]">{T.heroTitle2[language]}</span>
            </h1>
            <p className="text-xl text-[#475569]">{T.heroSub[language]}</p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-16">
            
            {/* Contact Info - 2 columns */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              {/* Logo & Company */}
              <div className="flex items-center gap-4 mb-8">
                <img src={DSB_LOGO} alt="DSB Business Solutions" className="h-14 w-auto" />
              </div>
              
              <div className="space-y-6 mb-10">
                <ContactItem icon={MapPin} label={T.address[language]} value="Hanauer Landstraße 291A, 60314 Frankfurt am Main" />
                <ContactItem icon={Mail} label={T.email[language]} value="info@dsb-deutschland.org" link="mailto:info@dsb-deutschland.org" />
                <ContactItem icon={Phone} label={T.phone[language]} value="02122 3298877" link="tel:+4921223298877" />
                <ContactItem icon={ExternalLink} label={T.website[language]} value="www.dsb-deutschland.org" link="https://www.dsb-deutschland.org" />
                <ContactItem icon={Clock} label={T.availability[language]} value={T.availVal[language]} />
              </div>

              {/* Management Card */}
              <div className="bg-[#0F1A2E] rounded-2xl p-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <Users size={20} className="text-[#F5A623]" />
                  <span className="text-white/60 text-sm uppercase tracking-wider">{T.mgmt[language]}</span>
                </div>
                <p className="font-semibold text-lg">Admir Basic & Alessandro Costantino</p>
                <p className="text-white/60 text-sm mt-2">{T.mgmtSub[language]}</p>
              </div>

              {/* Map */}
              <div className="mt-8 rounded-2xl overflow-hidden border border-[#e2e8f0] h-[200px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2558.5!2d8.71!3d50.11!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTDCsDA2JzM2LjAiTiA4wrA0MicyMi44IkU!5e0!3m2!1sde!2sde!4v1!5m2!1sde!2sde"
                  className="w-full h-full border-0"
                  allowFullScreen=""
                  loading="lazy"
                  title="Standort DSB Business Solutions"
                ></iframe>
              </div>
            </motion.div>

            {/* Contact Form - 3 columns */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <div className="bg-[#f8fafc] rounded-2xl border border-[#e2e8f0] p-8 md:p-10">
                <h3 className="text-2xl font-bold text-[#0F1A2E] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{T.formTitle[language]}</h3>
                <p className="text-[#475569] mb-8">{T.formSub[language]}</p>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[#0F1A2E] text-sm font-medium mb-2">{T.name[language]}</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full bg-white border border-[#e2e8f0] rounded-lg px-4 py-3 text-[#0F1A2E] placeholder-[#94a3b8] focus:border-[#0F1A2E] focus:outline-none focus:ring-2 focus:ring-[#0F1A2E]/10 transition-all"
                        placeholder="Max Mustermann"
                      />
                    </div>
                    <div>
                      <label className="block text-[#0F1A2E] text-sm font-medium mb-2">{T.company[language]}</label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="w-full bg-white border border-[#e2e8f0] rounded-lg px-4 py-3 text-[#0F1A2E] placeholder-[#94a3b8] focus:border-[#0F1A2E] focus:outline-none focus:ring-2 focus:ring-[#0F1A2E]/10 transition-all"
                        placeholder="Firma GmbH"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[#0F1A2E] text-sm font-medium mb-2">{T.emailLabel[language]}</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full bg-white border border-[#e2e8f0] rounded-lg px-4 py-3 text-[#0F1A2E] placeholder-[#94a3b8] focus:border-[#0F1A2E] focus:outline-none focus:ring-2 focus:ring-[#0F1A2E]/10 transition-all"
                        placeholder="ihre@email.de"
                      />
                    </div>
                    <div>
                      <label className="block text-[#0F1A2E] text-sm font-medium mb-2">{T.phonePh[language]}</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full bg-white border border-[#e2e8f0] rounded-lg px-4 py-3 text-[#0F1A2E] placeholder-[#94a3b8] focus:border-[#0F1A2E] focus:outline-none focus:ring-2 focus:ring-[#0F1A2E]/10 transition-all"
                        placeholder="+49 ..."
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-[#0F1A2E] text-sm font-medium mb-2">{T.topic[language]}</label>
                    <select
                      value={formData.topic}
                      onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                      className="w-full bg-white border border-[#e2e8f0] rounded-lg px-4 py-3 text-[#0F1A2E] focus:border-[#0F1A2E] focus:outline-none focus:ring-2 focus:ring-[#0F1A2E]/10 transition-all"
                    >
                      <option value="">Bitte wählen...</option>
                      <option value="datenschutz">Datenschutz & DSGVO</option>
                      <option value="it-sicherheit">IT-Sicherheit</option>
                      <option value="hr">HR-Beratung</option>
                      <option value="it-consulting">IT-Consulting</option>
                      <option value="organisation">Organisationsentwicklung</option>
                      <option value="compliance">Compliance</option>
                      <option value="sonstiges">Sonstiges</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-[#0F1A2E] text-sm font-medium mb-2">{T.message[language]}</label>
                    <textarea
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full bg-white border border-[#e2e8f0] rounded-lg px-4 py-3 text-[#0F1A2E] placeholder-[#94a3b8] focus:border-[#0F1A2E] focus:outline-none focus:ring-2 focus:ring-[#0F1A2E]/10 transition-all resize-none"
                      placeholder="Beschreiben Sie kurz Ihr Anliegen..."
                    ></textarea>
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full bg-[#0F1A2E] text-white py-4 font-bold tracking-wider uppercase flex items-center justify-center gap-2 hover:bg-[#F5A623] transition-colors rounded-lg"
                  >
                    {T.submit[language]} <Send size={18} />
                  </button>
                  <p className="text-[#94a3b8] text-xs text-center">{T.privacyNote[language]}</p>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}

function ContactItem({ icon: Icon, label, value, link }) {
  const Content = () => (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 bg-[#0F1A2E]/5 rounded-xl flex items-center justify-center flex-shrink-0">
        <Icon size={18} className="text-[#F5A623]" />
      </div>
      <div>
        <p className="text-[#94a3b8] text-xs uppercase tracking-wider mb-1">{label}</p>
        <p className="text-[#0F1A2E] font-medium">{value}</p>
      </div>
    </div>
  );

  if (link) {
    return (
      <a href={link} target={link.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="block hover:opacity-70 transition-opacity">
        <Content />
      </a>
    );
  }
  return <Content />;
}
